import type { ExtraStore, User } from "../services/UserService";

const NO_EXTRA_STORES: ExtraStore[] = [];

/**
 * Plan "extra stores": nearby stores the server adds on top of the ones the
 * user picked (read-only, server-derived). Home Depot extras are checked for
 * nearby stores' sale items; Walmart extras get a lighter daily scan of
 * in-store clearance, rollback and reduced-price items.
 */
export function getExtraStores(
  user: Pick<User, "homeDepotExtraStores" | "walmartExtraStores"> | null | undefined,
  retailer: string | null | undefined
): ExtraStore[] {
  // Returns the user's own array (or one shared empty array) so the result is
  // stable enough for hook dependency lists.
  if (!user) return NO_EXTRA_STORES;
  if (retailer === "home_depot") return user.homeDepotExtraStores ?? NO_EXTRA_STORES;
  if (retailer === "walmart") return user.walmartExtraStores ?? NO_EXTRA_STORES;
  return NO_EXTRA_STORES;
}

/** Store ids compare without leading zeros (HD catalog pads to 4 digits). */
export function formatExtraStoreId(storeId: string | number | undefined | null): string {
  if (storeId === undefined || storeId === null) return "";
  const id = String(storeId).trim();
  return id.replace(/^0+/, "") || id;
}

const clean = (value: unknown): string =>
  typeof value === "string" ? value.replace(/ /g, " ").trim() : "";

function catalogStoreId(store: any, retailer: string): string {
  if (!store) return "";
  if (retailer === "home_depot") return formatExtraStoreId(store.store_number);
  return formatExtraStoreId(store.store_id);
}

/** Finds a store in a Home Depot or Walmart catalog by id. */
export function findExtraCatalogStore(
  catalog: unknown,
  retailer: string,
  storeId: string | undefined | null
): any | undefined {
  const id = formatExtraStoreId(storeId);
  if (!id || !Array.isArray(catalog)) return undefined;
  return catalog.find((store) => catalogStoreId(store, retailer) === id);
}

/**
 * City of a catalog store. Home Depot rows carry `city`; Walmart rows usually
 * don't, so it's read from a "street, City, ST 12345" address when present.
 */
export function extraCatalogStoreCity(store: any, retailer: string): string | undefined {
  if (!store) return undefined;
  const city = clean(store.city);
  if (city) return city;
  if (retailer !== "walmart") return undefined;
  const parts = clean(store.address).split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3 && /^[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/.test(parts[parts.length - 1])) {
    return parts[parts.length - 2];
  }
  return undefined;
}

/** Street line of a catalog store. */
export function extraCatalogStoreStreet(store: any, retailer: string): string | undefined {
  if (!store) return undefined;
  if (retailer === "home_depot") return clean(store.address_1) || undefined;
  return clean(store.address).split(",")[0]?.trim() || undefined;
}

/**
 * Card title for an extra store: its city, or for Walmart rows without a
 * parseable city the catalog address (how saved Walmart stores are named).
 */
export function extraCatalogStoreName(store: any, retailer: string): string | undefined {
  if (!store) return undefined;
  if (retailer === "home_depot") return clean(store.city) || undefined;
  return extraCatalogStoreCity(store, retailer) || clean(store.address) || undefined;
}
